import {
  addDays,
  addHours,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  endOfDay,
  endOfHour,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isBefore,
  parse,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { flatten, mapValues, range } from 'lodash-es'
import { TimeseriesGranularity } from '@percept/types'

export const getFinancialYearStart = (date: Date, startMonth = 4): Date => {
  const startMonthIndex = startMonth - 1
  const YYYY = date.getUTCFullYear()
  const financialYYYY = (
    date.getUTCMonth() < startMonthIndex ?
      YYYY - 1 :
      YYYY
  )
  const financialYearStart = new Date(financialYYYY, startMonthIndex, 1)
  return financialYearStart
}

export const parseFinancialYearStart = (fyLabel: string): Date => {
  const trimmed = fyLabel.replace('/', '').replace('FY', '').trim()
  if( trimmed.length !== 4 ){
    throw new Error(`Could not parse FY label: '${fyLabel}'`)
  }
  const financialYYYY = Number(`20${trimmed.slice(0, 2)}`)
  return new Date(financialYYYY, 3, 1)
}

const getMonthIndexForFinancialYear = (date: Date): number => {
  let monthIndex = date.getUTCMonth()
  if( monthIndex < 3 ){
    return monthIndex + 9
  }
  return monthIndex -= 3
}

export const getMonthIndicesForFinancialYear = ({
  startMonth,
  endMonth
}: {
  startMonth: string;
  endMonth: string;
}): number[] => {
  const referenceDate = new Date()
  const minMonth = parse(startMonth, 'MMM', referenceDate)
  const maxMonth = parse(endMonth, 'MMM', referenceDate)
  const fyStartMonthIndex = getMonthIndexForFinancialYear(minMonth)
  const fyEndMonthIndex = getMonthIndexForFinancialYear(maxMonth)
  return range(fyStartMonthIndex, fyEndMonthIndex + 1)
}

export const getMonthRangeForFinancialYear = ({
  startMonth,
  startYear,
  endMonth,
  endYear,
  maxDate = null,
}: {
  startMonth: string;
  startYear: number;
  endMonth: string;
  endYear: number;
  maxDate?: Date | null;
}): Date[] => {

  const fyMonthIndices = getMonthIndicesForFinancialYear({ startMonth, endMonth })

  const canonicalMaxDate = maxDate || new Date()

  const reportDates = flatten(
    range(startYear, endYear + 1).reduce( (acc, yyyy) => {
      fyMonthIndices.forEach( monthIndex => {
        const normalisedMonthIndex = monthIndex < 9 ? monthIndex + 3 : monthIndex - 9
        const resolvedYYYY = monthIndex >= 9 ? yyyy + 1 : yyyy
        const normalisedDate = new Date(resolvedYYYY, normalisedMonthIndex, 1)
        if( isBefore(normalisedDate, canonicalMaxDate) ){
          acc.push(normalisedDate)
        }
      })
      return acc
    }, [] as Date[])
  )

  return reportDates
}

export const UTC_TIMEZONE = 'Etc/UTC'

export const ISO_DATE_FORMAT = 'yyyy-MM-dd'

export const isoDate = (date: Date | number | string): string => (
  format(date, ISO_DATE_FORMAT)
)

export const formatUTC = (date: Date | number | string, formatString: string): string => (
  formatInTimeZone(date, UTC_TIMEZONE, formatString)
)

// Alias for easier distinction between UTC and local time
export const formatLocal = format

export const isoDateUTC = (date: Date | number | string): string => (
  formatUTC(date, ISO_DATE_FORMAT)
)

export const getEpochShiftedDate = (date: Date | number | string): Date => {
  // Normalises to UTC where timezone offsets can result in the date being
  // interpreted as the previous day.
  // NOTE - should _not_ be used with any dates where time matters.
  const clone = new Date(date)
  clone.setTime(clone.getTime() + (clone.getTimezoneOffset() * 60 * 1000))
  return clone
}

export const getReverseEpochShiftedDate = (date: Date | number | string): Date => {
  // Normalises back from UTC. Required as date-fns date operations will
  // set times to 00:00 or 23:59 etc when computing start and end of periods,
  // so this may be needed to shift dates back again when we're expecting
  // to then format dates as UTC.
  const clone = new Date(date)
  clone.setTime(clone.getTime() - (clone.getTimezoneOffset() * 60 * 1000))
  return clone
}

export const getTimeAgnosticDate = (date: Date | number | string): Date => {
  // Simplifies timezone-related issues by epoch shifting to 12:00 UTC, guaranteeing
  // that date-only parsing gives us back equivalent values for UTC dd-mm-yyyy.
  // As the name implies, should never be used with any dates where time matters.
  const epochShifted = getEpochShiftedDate(date)
  epochShifted.setHours(12)
  return getReverseEpochShiftedDate(epochShifted)
}


type DateFnsRestArguments<F extends (...args: any) => any> = (
  F extends (date: Date | number,...args: infer Args) => any ? Args : never
)

export const applyDateFnsOp = <T extends (date: Date | number, ...args: any[]) => any>(
  op: T,
  date: Date | number,
  ...args: DateFnsRestArguments<T>
): Date => {
  const shifted = getEpochShiftedDate(date)
  const altered = op(shifted, ...args)
  return getReverseEpochShiftedDate(altered)
}

export const getDateFnsOp = <T extends (date: Date | number, ...args: any[]) => any>(
  op: T,
): (date: Date | number, ...args: DateFnsRestArguments<T>) => Date => {
  return (date, ...rest): Date => (
    applyDateFnsOp(op, date, ...rest)
  )
}

const originalDateFnsOps = {
  addDays,
  addHours,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  endOfDay,
  endOfHour,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
}

export const dateFnsOps = mapValues(originalDateFnsOps, v => getDateFnsOp(v))


const dateIncrementMethodMapping: Record<TimeseriesGranularity, (date: Date | number, amount: number) => Date> = {
  DAY: addDays,
  WEEK: (date, amount) => addWeeks(startOfWeek(date, { weekStartsOn: 1}), amount),
  MONTH: (date, amount) => addMonths(startOfMonth(date), amount),
  QUARTER: addQuarters,
  CALENDAR_YEAR: addYears,
  FINANCIAL_YEAR: addYears,
}


export const getDateLabelsForGranularity = (
  [start, end]: [Date | null, Date | null],
  granularity: TimeseriesGranularity = 'DAY'
): string[] => {
  const values: string[] = []
  if (start && end) {
    const endTime = end.getTime()
    let current = getEpochShiftedDate(start)
    const incrementMethod = dateIncrementMethodMapping[granularity]
    while( current.getTime() <= endTime ){
      values.push(isoDate(current))
      current = incrementMethod(current, 1)
    }
  }
  return values
}


export const getPostgresNormalisedDateLabelsForGranularity = (
  [start, end]: [Date | null, Date | null],
  granularity: TimeseriesGranularity = 'DAY'
): string[] => {
  /*
  The Postgres date_trunc function truncates a date to the Monday of the given date's
  week.
  The start/end date query filters will usually be on the true date, not the truncated
  date, so we need to include a label for the week that 'start' is closest to, even
  if that date is before 'start'.
  */
  const values: string[] = []
  if (start && end) {
    const endTime = end.getTime()
    let current = getEpochShiftedDate(start)
    if( granularity === 'WEEK' ){
      current = startOfWeek(current, { weekStartsOn: 1 })
    }
    const incrementMethod = dateIncrementMethodMapping[granularity]
    while( current.getTime() <= endTime ){
      values.push(isoDate(current))
      current = incrementMethod(current, 1)
    }
  }
  return values
}
